"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation } from "convex/react";
import type { FunctionArgs } from "convex/server";
import { api } from "@convex/_generated/api";
import type { DeliveryLocation } from "@/lib/checkout/delivery-location";
import { handleError } from "@/lib/handle-error";

export const ADDRESS_SAVED_MESSAGE = "Saved to your addresses.";
export const ADDRESS_SAVE_FAILED_MESSAGE =
  "We couldn't add this address to your saved addresses. It will still be used for this order.";

interface SavedAddressCandidate {
  id: string;
  location: DeliveryLocation;
}

type StreetLocation = DeliveryLocation & { line1: string };

function hasStreet(location: DeliveryLocation): location is StreetLocation {
  return !!location.line1?.trim();
}

function streetKey(street: Pick<StreetLocation, "line1" | "zip">): string {
  const line1 = street.line1.trim().replace(/\s+/g, " ").toUpperCase();
  return `${line1}|${street.zip.slice(0, 5)}`;
}

export function findSavedStreetAddress(
  options: SavedAddressCandidate[],
  street: Pick<StreetLocation, "line1" | "zip">,
): SavedAddressCandidate | undefined {
  const key = streetKey(street);
  return options.find(
    (option) =>
      !!option.location.line1 &&
      streetKey({ line1: option.location.line1, zip: option.location.zip }) ===
        key,
  );
}

function toCreateAddressArgs(
  location: StreetLocation,
): FunctionArgs<typeof api.addresses.mutations.create> {
  return {
    ownerType: "user",
    addressType: "shipping",
    line1: location.line1.trim(),
    line2: location.line2?.trim() || undefined,
    city: location.city,
    state: location.state,
    postalCode: location.zip,
    country: "US",
    latitude: location.latitude,
    longitude: location.longitude,
  };
}

export function useSaveDeliveryAddress(canSave: boolean) {
  const createAddress = useMutation(api.addresses.mutations.create);
  const [notice, setNotice] = useState<string | null>(null);
  const inFlightRef = useRef(new Map<string, Promise<string | null>>());

  const save = useCallback(
    (
      location: DeliveryLocation,
      options: SavedAddressCandidate[],
    ): Promise<string | null> => {
      if (!canSave || !hasStreet(location)) return Promise.resolve(null);
      const existing = findSavedStreetAddress(options, location);
      if (existing) return Promise.resolve(existing.id);
      const key = streetKey(location);
      const inFlight = inFlightRef.current.get(key);
      if (inFlight) return inFlight;
      const attempt = createAddress(toCreateAddressArgs(location))
        .then((savedId) => {
          setNotice(ADDRESS_SAVED_MESSAGE);
          return savedId;
        })
        .catch((thrown: unknown) => {
          handleError(thrown, {
            display: "silent",
            fallbackMessage: ADDRESS_SAVE_FAILED_MESSAGE,
          });
          setNotice(ADDRESS_SAVE_FAILED_MESSAGE);
          return null;
        })
        .finally(() => {
          inFlightRef.current.delete(key);
        });
      inFlightRef.current.set(key, attempt);
      return attempt;
    },
    [canSave, createAddress],
  );

  const clearNotice = useCallback(() => setNotice(null), []);

  return useMemo(
    () => ({ save, notice, clearNotice }),
    [save, notice, clearNotice],
  );
}
